import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { BulkEmailPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';
import { TemplateFilter } from './constants';

type Props = {
  activeFilter: TemplateFilter;
  onCreateTemplate: () => void;
  onFilterChange: (filter: TemplateFilter) => void;
  onSearchChange: (query: string) => void;
  searchQuery: string;
};

type FilterChip = {
  label: string;
  value: TemplateFilter;
};

export const TemplateToolbar = ({
  activeFilter,
  onCreateTemplate,
  onFilterChange,
  onSearchChange,
  searchQuery,
}: Props) => {
  const { t } = useTranslation('bulk-messaging');
  const isMobileToolbar = useMatchMedia({ maxWidth: breakpoints.xsmall.max });

  const chips: FilterChip[] = [
    { label: t('All'), value: 'all' },
    { label: t('Custom Templates'), value: 'custom' },
    { label: t('Weave Templates'), value: 'weave' },
  ];

  return (
    <div css={[toolbarStyle, isMobileToolbar && toolbarMobileStyle]}>
      {/* Filter chips — left side */}
      <div css={chipsContainerStyle}>
        {chips.map(({ label, value }) => (
          <button
            key={value}
            css={[chipStyle, activeFilter === value && chipActiveStyle]}
            onClick={() => onFilterChange(value)}
            data-trackingid={`${BulkEmailPrefixes.Templates}-compose-filter-${value}-btn`}
          >
            <Text
              size='medium'
              css={{
                color: activeFilter === value ? theme.colors.primary50 : theme.colors.neutral70,
                fontWeight: activeFilter === value ? theme.font.weight.bold : theme.font.weight.regular,
              }}
            >
              {label}
            </Text>
          </button>
        ))}
      </div>

      {/* Search + Create — right side */}
      <div css={[rightSideStyle, isMobileToolbar && rightSideMobileStyle]}>
        <div css={[searchWrapperStyle, isMobileToolbar && searchWrapperMobileStyle]}>
          <span css={searchIconStyle}>
            <Icon name='search' size={16} color='subdued' />
          </span>
          <input
            type='text'
            placeholder={t('Search')}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            css={searchInputStyle}
            data-trackingid={`${BulkEmailPrefixes.Templates}-compose-search-input`}
          />
        </div>

        {isMobileToolbar ? (
          <Button
            variant='secondary'
            size='large'
            iconName='plus'
            label={t('Create Template')}
            onClick={onCreateTemplate}
            trackingId={`${BulkEmailPrefixes.Templates}-compose-create-template-btn`}
            css={createButtonIconOnlyStyle}
          />
        ) : (
          <Button
            variant='secondary'
            size='large'
            iconName='plus'
            onClick={onCreateTemplate}
            trackingId={`${BulkEmailPrefixes.Templates}-compose-create-template-btn`}
            css={createButtonStyle}
          >
            {t('Create Template')}
          </Button>
        )}
      </div>
    </div>
  );
};

const toolbarStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  flexWrap: 'wrap' as const,
};

const toolbarMobileStyle = {
  flexDirection: 'column' as const,
  alignItems: 'stretch' as const,
};

const chipsContainerStyle = {
  display: 'flex',
  gap: theme.spacing(1),
  flexWrap: 'wrap' as const,
};

const chipStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: theme.spacing(5),
  padding: theme.spacing(0, 1),
  border: `1px solid ${theme.colors.neutral30}`,
  borderRadius: theme.borderRadius.small,
  backgroundColor: 'transparent',
  cursor: 'pointer',
  transition: 'border-color 0.15s ease, background-color 0.15s ease',
  '&:hover': {
    borderColor: theme.colors.primary50,
    backgroundColor: theme.colors.primary5,
  },
};

const chipActiveStyle = {
  border: `1px solid ${theme.colors.primary50}`,
  backgroundColor: theme.colors.primary5,
};

const rightSideStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
};

const rightSideMobileStyle = {
  width: '100%',
  minWidth: 0,
};

const searchWrapperStyle = {
  position: 'relative' as const,
  display: 'flex',
  alignItems: 'center',
  width: theme.spacing(40),
  flex: '0 0 auto',
};

const searchWrapperMobileStyle = {
  flex: '1 1 auto',
  width: 'auto',
  minWidth: 0,
};

const searchIconStyle = {
  position: 'absolute' as const,
  left: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  pointerEvents: 'none' as const,
};

const searchInputStyle = {
  width: '100%',
  height: theme.spacing(5),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(1.5),
  border: `1px solid ${theme.colors.neutral30}`,
  borderRadius: theme.borderRadius.small,
  fontSize: theme.font.size.large,
  fontFamily: theme.font.family,
  color: theme.colors.neutral70,
  backgroundColor: theme.colors.white,
  outline: 'none',
  '&::placeholder': {
    color: theme.colors.neutral50,
  },
  '&:focus': {
    borderColor: theme.colors.primary50,
    boxShadow: `0 0 0 2px ${theme.colors.primary10}`,
  },
};

const createButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  whiteSpace: 'nowrap' as const,
};

const createButtonIconOnlyStyle = {
  flexShrink: 0,
};
